import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext"; // adjust path if needed

/* ==============================
   Helpers
============================== */
function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatWeek(weekStart) {
  if (!weekStart) return "—";
  try {
    const d = new Date(weekStart);
    const end = new Date(d);
    end.setDate(d.getDate() + 6);
    return `${d.toDateString()} – ${end.toDateString()}`;
  } catch {
    return weekStart;
  }
}

export default function JambLeaderboardPage() {
  const navigate = useNavigate();
const { user } = useAuth(); // { id, role, ... }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [weekStart, setWeekStart] = useState(null);

  // Pagination (frontend-only, since backend caps at 50)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ==============================
     Load leaderboard + my rank
  ============================== */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [lbRes, myRes] = await Promise.allSettled([
          api.get("/api/jamb/leaderboard"),
          api.get("/api/jamb/leaderboard/me"),
        ]);

        if (lbRes.status === "fulfilled") {
          setLeaderboard(lbRes.value.data?.leaderboard || []);
          setWeekStart(lbRes.value.data?.week_start || null);
        } else {
          throw new Error("Leaderboard fetch failed");
        }

        if (myRes.status === "fulfilled") {
          setMyRank(myRes.value.data);
        } else {
          // 404 = user did not attempt this week
          setMyRank(null);
        }
      } catch (e) {
        setError("Unable to load JAMB leaderboard at this time.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* ==============================
     Pagination slice
  ============================== */
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return leaderboard.slice(start, start + rowsPerPage);
  }, [leaderboard, page, rowsPerPage]);

  /* ==============================
     Loading / Error
  ============================== */
  if (loading) {
    return (
      <Box mt={6} display="flex" justifyContent="center">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={1000} mx="auto" mt={4} p={2}>
        <Alert severity="error">{error}</Alert>
        <Box mt={2}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxWidth={1100} mx="auto" p={2}>
      {/* ================= Header ================= */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            🏆 JAMB Weekly Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {weekStart ? `Week: ${formatWeek(weekStart)}` : "Current Week"}
          </Typography>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
       <Button
  variant="outlined"
  onClick={() => {
    if (user?.role === "admin") {
      navigate("/admin/jamb/jamb-history");
    } else {
      navigate("/dashboard/jamb/my-jamb-history");
    }
  }}
>
  📜 JAMB History
</Button>

        </Box>
      </Box>

      {/* ================= My Rank ================= */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography fontWeight={700} gutterBottom>
          👤 My Rank This Week
        </Typography>

        {myRank ? (
          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip label={`Rank: #${myRank.rank}`} color="primary" />
            <Chip label={`Score: ${myRank.score} / 400`} />
            <Chip
              label={
                myRank.accuracy != null
                  ? `Accuracy: ${myRank.accuracy}%`
                  : "Accuracy: --"
              }
            />
            <Chip label={`Time: ${formatTime(myRank.time_used)}`} />
          </Box>
        ) : (
          <Typography color="text.secondary">
            You did not participate in this week’s JAMB challenge.
          </Typography>
        )}
      </Paper>

      {/* ================= Leaderboard Table ================= */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Accuracy</TableCell>
                <TableCell>Time Used</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No completed JAMB attempts yet this week.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row) => {
                  const isMe = myRank && row.rank === myRank.rank;

                  return (
                    <TableRow
                      key={row.rank}
                      sx={{
                        backgroundColor: isMe
                          ? "rgba(25,118,210,0.08)"
                          : "inherit",
                      }}
                    >
                <TableCell fontWeight={700}>
  {Number(row.rank) === 1 && "🥇 "}
  {Number(row.rank) === 2 && "🥈 "}
  {Number(row.rank) === 3 && "🥉 "}
  {Number(row.rank)}
</TableCell>


                      <TableCell>
                        {row.full_name
                          ? `${row.full_name.split(" ")[0]} ${
                              row.full_name.split(" ")[1]?.[0] || ""
                            }.`
                          : "—"}
                      </TableCell>

                      <TableCell>{row.score}</TableCell>

                      <TableCell>
                        {row.accuracy != null ? `${row.accuracy}%` : "--"}
                      </TableCell>

                      <TableCell>{formatTime(row.time_used)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ================= Pagination ================= */}
        <TablePagination
          component="div"
          count={leaderboard.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </Paper>
    </Box>
  );
}
